import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { appsApi } from '../api/apps.api';

/**
 * La app seleccionada, global a todo el backoffice.
 *
 * Existe porque noten es una superficie de PLATAFORMA que opera datos POR APP:
 * preferencias, opt-outs, budgets, templates, webhooks y el estado de un
 * usuario cuelgan todos de un `appId`. Desde #148 la sesión de superusuario no
 * aporta ninguna, así que sin esto las lecturas mezclaban todas las apps y las
 * escrituras respondían 403 APP_CONTEXT_REQUIRED. Ese fue el síntoma de
 * "no veo los templates, la query devuelve 0": el backoffice estaba mirando
 * otra app.
 *
 * Vive en un contexto y no en cada pantalla porque la pregunta "¿sobre qué app
 * estoy trabajando?" no cambia al navegar, y tenerla por pantalla obligaba a
 * re-elegirla en cada una.
 */

const STORAGE_KEY = 'noten.selectedAppId';

interface SelectedAppValue {
  appId: string;
  setAppId: (appId: string) => void;
  /** true cuando no hay app elegida: las escrituras por app no son posibles. */
  isUnset: boolean;
  /**
   * true cuando la app elegida NO tiene tenants (idachu `tenancy: 'none'`,
   * como resuelto). Las pantallas no deben pedirlos ni ofrecerlos.
   * Con `isUnset` no se sabe todavia, y se asume que si los tiene.
   */
  hasNoTenants: boolean;
}

const SelectedAppContext = createContext<SelectedAppValue | null>(null);

function readStored(): string {
  // localStorage puede tirar (modo privado, cookies de sitio bloqueadas) o
  // directamente NO existir — pasa en jsdom con esta config, y por eso el
  // catch cubre tambien el TypeError de acceder a `.getItem` de undefined.
  // Nada de esto puede tumbar el backoffice entero.
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function SelectedAppProvider({ children }: { children: ReactNode }) {
  const [appId, setAppIdState] = useState<string>(readStored);
  // La lista ya viene cacheada por el selector del header; esto no agrega un
  // request extra por pantalla.
  const { data: apps } = appsApi.useList();

  const setAppId = useCallback((next: string) => {
    setAppIdState(next);
    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, next);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // La selección sigue viva en memoria; solo no sobrevive al refresh.
    }
  }, []);

  const value = useMemo<SelectedAppValue>(() => {
    const selected = (Array.isArray(apps) ? apps : []).find((a) => a.id === appId);
    return {
      appId,
      setAppId,
      isUnset: appId === '',
      // Sin app elegida o mientras carga, se asume que tiene tenants: pedir uno
      // de mas molesta, pero darlo por inexistente cuando si hay escribiria la
      // preferencia en el alcance equivocado.
      hasNoTenants: selected?.tenancy === 'none',
    };
  }, [appId, setAppId, apps]);

  return <SelectedAppContext.Provider value={value}>{children}</SelectedAppContext.Provider>;
}

export function useSelectedApp(): SelectedAppValue {
  const ctx = useContext(SelectedAppContext);
  if (!ctx) {
    throw new Error('useSelectedApp debe usarse dentro de <SelectedAppProvider>');
  }
  return ctx;
}

/**
 * Query por app para react-proto-kit: `{}` cuando no hay app elegida, para
 * que los listados sigan siendo globales en vez de romper.
 */
export function appScopedQuery(appId: string): Record<string, string> {
  return appId ? { appId } : {};
}
